import { useQuery, useQueryErrorResetBoundary } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useLocation,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { AlertCircle } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tab, TabList, Tabs } from "@/components/application/tabs/tabs";
import { Avatar } from "@/components/Atoms/Avatar/Avatar";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { NativeSelect } from "@/components/base/select/select-native";
import { Drawer } from "@/components/Molecules/Drawer/Drawer";
import { TabContentWrapper } from "@/components/Molecules/TabContentWrapper";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAppTranslation } from "@/hooks/use-app-translation";
import {
	OrganizationStatusColorMap,
	organizationQueryOptions,
} from "@/services/organization";

export const ModeSchema = z.enum(["edit", "view"]);
export const TabSchema = z.enum(["details", "contacts", "users", "activity"]);

export const SearchSchema = z.object({
	mode: ModeSchema.optional().default("view"),
	tab: TabSchema.optional(),
});

export type Mode = z.infer<typeof ModeSchema>;
export type Tab = z.infer<typeof TabSchema>;
export type Search = z.infer<typeof SearchSchema>;

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/organizations/$organizationId",
)({
	component: RouteComponent,
	params: {
		parse: (params) => ({
			organizationId: z.string().parse(params.organizationId),
		}),
	},
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
	beforeLoad: ({ params, location, context }) => {
		const isParentRoute = location.pathname.endsWith(
			`/organizations/${params.organizationId}`,
		);

		if (isParentRoute) {
			throw redirect({
				to: "/admin/organizations/$organizationId/details",
				params: { organizationId: params.organizationId },
				search: { mode: "view" },
			});
		}

		// Do not await. A pending beforeLoad suspends up to the authenticated
		// layout, which replaces the whole app with a full-screen white loader.
		void context.queryClient.prefetchQuery(
			organizationQueryOptions.detail(params.organizationId),
		);
		void context.queryClient.prefetchQuery(
			organizationQueryOptions.users.count(params.organizationId),
		);
		void Promise.all([
			context.queryClient.prefetchQuery(
				organizationQueryOptions.contacts.list(params.organizationId),
			),
			context.queryClient.prefetchInfiniteQuery({
				...organizationQueryOptions.users.infinite(
					params.organizationId,
					{
						OrderBy: {
							Fields: ["User.LastName", "User.FirstName"],
							Descending: false,
						},
					},
					50,
					undefined,
				),
			}),
		]).catch(() => {
			// Tabs fetch their own data if prefetch fails.
		});
	},
});

function RouteComponent() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/organizations/$organizationId",
	});
	const [isOpen, setIsOpen] = useState(false);
	const queryErrorResetBoundary = useQueryErrorResetBoundary();

	const currentTab = (location.pathname.split("/").pop() as Tab) || "details";

	const orgById = useQuery(
		organizationQueryOptions.detail(params.organizationId),
	);
	const isLoading = orgById.isLoading;
	const isError = orgById.isError;

	useEffect(() => {
		setIsOpen(true);
	}, []);

	useEffect(() => {
		queryErrorResetBoundary.reset();
	}, [queryErrorResetBoundary]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			navigate({
				to: "/admin/organizations",
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			handleClose();
		}
	};

	const title = isLoading
		? t("admin.organizations.drawer.orgName")
		: orgById.data?.name;
	const description = t("admin.organizations.drawer.description");
	const tabs = [
		{ label: t("admin.organizations.drawer.details"), id: "details" },
		{ label: t("admin.organizations.drawer.contacts"), id: "contacts" },
		{ label: t("admin.organizations.drawer.users"), id: "users" },
		{ label: t("admin.organizations.drawer.activity"), id: "activity" },
	];

	return (
		<Drawer
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			width="40%"
			backdropPreset="transparent"
			dialogClassName="gap-0 overflow-hidden"
		>
			{({ close }) => (
				<>
					<Drawer.Header
						variant="document"
						icon={
							<Avatar
								placeholder="organization"
								size="md"
								alt={title ?? undefined}
							/>
						}
						iconSize="md"
						iconTheme="light"
						title={
							<div className="flex items-center flex-row gap-4">
								<span>{title}</span>
								{!isLoading && !isError && orgById.data?.status?.name ? (
									<BadgeWithDot
										size="sm"
										type="modern"
										color={
											OrganizationStatusColorMap[
												orgById.data.status.code ?? -1
											] ?? "blue"
										}
									>
										{orgById.data.status.name}
									</BadgeWithDot>
								) : null}
							</div>
						}
						description={description}
						showCloseButton
						closeButtonClassName="absolute top-2 right-2 z-10"
						onClose={() => {
							close();
							handleClose();
						}}
					/>
					<Drawer.ScrollBody showPlaceholder={false}>
						{isLoading ? (
							<div className="flex w-full flex-1 items-center justify-center">
								<LoadingIndicator
									type="line-simple"
									size="xl"
									supportingText
								/>
							</div>
						) : isError ? (
							<div className="flex flex-col items-center justify-center h-full gap-4">
								<Illustration type="cloud" size="sm">
									<div className="bg-error-500 rounded-full p-3">
										<AlertCircle className="size-6" />
									</div>
								</Illustration>
								<div className="flex flex-col items-center justify-center pt-4">
									<p className="text-gray-900 font-semibold text-md">
										{t("admin.organizations.errors.errorLoadingPage")}
									</p>
									<div className="w-[60%]">
										<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
											{t("admin.organizations.errors.errorLoadingContent")}
										</p>
									</div>
								</div>
							</div>
						) : (
							<div className="flex w-full flex-col">
								<NativeSelect
									aria-label={t("admin.organizations.drawer.tabs")}
									value={currentTab}
									onChange={(event) =>
										navigate({
											to: `/admin/organizations/$organizationId/${event.target.value}`,
											params: { organizationId: params.organizationId },
											search: { mode: "view" },
										})
									}
									options={tabs.map((tab) => ({
										label: tab.label,
										value: tab.id,
									}))}
									className="mx-6 mt-3 w-80 md:hidden"
								/>
								<div className="flex h-12 w-full shrink-0 flex-col items-stretch border-b border-secondary bg-primary px-(--spacing-3xl,24px) max-md:hidden">
									<Tabs
										selectedKey={currentTab}
										onSelectionChange={(event) =>
											navigate({
												to: `/admin/organizations/$organizationId/${event}`,
												params: { organizationId: params.organizationId },
												search: { mode: "view" },
											})
										}
										className="h-full w-full"
									>
										<TabList
											type="underline"
											size="sm"
											items={tabs}
											className="h-full w-full items-stretch gap-(--spacing-lg,12px) border-b-0"
										>
											{(tab) => (
												<Tab
													id={tab.id}
													label={tab.label}
													className="h-full items-end -mb-px"
												/>
											)}
										</TabList>
									</Tabs>
								</div>
								<div className="flex w-full flex-col gap-(--spacing-xs,4px) p-(--spacing-xl,16px)">
									<TabContentWrapper tabKey={currentTab}>
										<Outlet />
									</TabContentWrapper>
								</div>
							</div>
						)}
					</Drawer.ScrollBody>
				</>
			)}
		</Drawer>
	);
}
